import { Component, inject, OnInit, signal, computed, effect, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, Subject, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DynamicFilterConfig, DynamicFilterValues, EnumValues, FilterOption, createDynamicFilterState } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PaginatorState } from 'primeng/paginator';
import _ from 'lodash';
import { EnumService } from '../../../services/enum.service';
import { KnittingOrder } from '../../../models/production/knitting-order';
import { Requisition, RequisitionFilters, RequisitionSearchCommand } from '../../../models/requisitions/requisition';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { KnittingOrderProxyService } from '../../../proxies/production/knitting-order.proxy.service';
import { RequisitionProxyService } from '../../../proxies/inventory/requisition.proxy.service';

@Component({
  selector: 'app-yarn-requisitions',
  templateUrl: './yarn-requisitions.component.html',
  styleUrl: './yarn-requisitions.component.scss',
})
export class YarnRequisitionsComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);
  private requisitionProxyService = inject(RequisitionProxyService);
  private knittingOrderProxyService = inject(KnittingOrderProxyService);

  private destroy$ = new Subject<void>();
  private appliedFilters: RequisitionFilters = {};
  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  requisitions = signal<Requisition[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'yarn-requisitions-filters', config: this.filterConfig });

  unitOfMeasure: EnumValues;
  requisitionStatus = signal<EnumValues>({});
  requisitionType = signal<EnumValues>({});
  enumsReady = signal(false);

  listKnittingOrders = signal<KnittingOrder[]>([]);
  filteredKnittingOrders = signal<KnittingOrder[]>([]);

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;

  statusOptions = computed<FilterOption[]>(() => {
    const statuses = this.requisitionStatus();
    return Object.keys(statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        value: Number(key),
        label: statuses[Number(key)],
      }));
  });

  requisitionTypeOptions = computed<FilterOption[]>(() => {
    const types = this.requisitionType();
    return Object.keys(types)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        value: Number(key),
        label: types[Number(key)],
      }));
  });

  canEdit = computed(() => {
    if (
      AuthService.hasRole(UserGroup.InternalYarnRequisitionKnittingEditor) ||
      AuthService.hasRole(UserGroup.InternalYarnRequisitionAdmin)
    ) {
      return true;
    }
    if (
      AuthService.hasRole(UserGroup.InternalYarnRequisitionAllViewer) ||
      AuthService.hasRole(UserGroup.InternalYarnRequisitionKnittingViewer) ||
      AuthService.hasRole(UserGroup.InternalYarnRequisitionWarehouseEditor) ||
      AuthService.hasRole(UserGroup.InternalYarnRequisitionWarehouseViewer)
    ) {
      return false;
    }
    return false;
  });

  knittingOrdersMap: Record<string, KnittingOrder> = {};

  getRowData(rowData: unknown): Requisition {
    return rowData as Requisition;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    this.unitOfMeasure = this.enumService.getEnum('UnitOfMeasure', 'item').values;

    effect(
      () => {
        this.enumService.enumsVersion();
        const statusesValue = this.enumService.getEnum('RequisitionStatus', 'inventory').values;
        const typesValue = this.enumService.getEnum('RequisitionType', 'inventory').values;
        if (Object.keys(statusesValue).length > 0) this.requisitionStatus.set(statusesValue);
        if (Object.keys(typesValue).length > 0) {
          this.requisitionType.set(typesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadInitialData();

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  loadInitialData(): void {
    this.startLoading();
    this.knittingOrderProxyService
      .search({ statuses: [2, 3] })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          const knittingOrders = (result?.data?.items || []).map((ko) => ({
            ...ko,
          }));

          this.listKnittingOrders.set(knittingOrders);
          this.filteredKnittingOrders.set(knittingOrders);

          this.knittingOrdersMap = this.listKnittingOrders().reduce(
            (acc, ko) => {
              if (ko.id) {
                acc[ko.id] = ko;
              }
              return acc;
            },
            {} as Record<string, KnittingOrder>,
          );
        },
        error: () => {
          this.listKnittingOrders.set([]);
          this.filteredKnittingOrders.set([]);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'requisitionNumber',
          label: translate('Yarn_Requisitions_Number', 'Requisition Number'),
          placeholder: translate('Enter_requisition_number', 'Enter requisition number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'knittingOrder',
          label: translate('Knitting_Order', 'Knitting Order'),
          placeholder: translate('Select_knitting_order', 'Select knitting order'),
          autocompleteConfig: {
            suggestions: this.filteredKnittingOrders(),
            optionLabel: 'knittingOrderNumber',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterKnittingOrders(event),
            resolveById: (id: string) => this.resolveKnittingOrder(id),
            dropdown: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Yarn_Requisitions_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.statusOptions(),
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'multiselect',
          formControlName: 'requisitionTypes',
          label: translate('Requisition_Type', 'Requisition Type'),
          placeholder: translate('Select_requisition_type', 'Select requisition type'),
          options: this.requisitionTypeOptions(),
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveKnittingOrder(id: string): Observable<KnittingOrder | null> {
    return this.knittingOrderProxyService.getById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  search() {
    const formValues = this.appliedFilters;
    const requisitionNumber = formValues.requisitionNumber?.trim();
    const knittingOrderId = typeof formValues.knittingOrder === 'string' ? formValues.knittingOrder : formValues.knittingOrder?.id;

    const statusesRaw = formValues.statuses;
    const statuses = Array.isArray(statusesRaw)
      ? statusesRaw.map((s): number => Number(typeof s === 'object' && 'value' in s ? s.value : s))
      : [];

    const typesRaw = formValues.requisitionTypes;

    const types = Array.isArray(typesRaw) ? typesRaw.map((t): number => Number(typeof t === 'object' && 'value' in t ? t.value : t)) : [];

    const command: RequisitionSearchCommand = {
      requisitionNumber: requisitionNumber || undefined,
      initiator: knittingOrderId
        ? {
            entityDefinitionCode: 6,
            reference: knittingOrderId,
          }
        : undefined,
      statuses: statuses && statuses.length > 0 ? statuses : undefined,
      types: types && types.length > 0 ? types : undefined,
    };

    this.startLoading();
    this.requisitionProxyService
      .search(command, this.currentPage, this.pageSize)
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (result) => {
          this.requisitions.set(result?.data?.items || []);
          this.totalRecords = result?.data?.totalCount || 0;
        },
        error: () => {
          this.requisitions.set([]);
          this.totalRecords = 0;
        },
      });
  }

  onPageChange(event: PaginatorState) {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || this.pageSize;
    this.search();
  }

  filterKnittingOrders(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.listKnittingOrders().filter((ko) => ko.knittingOrderNumber?.toLowerCase().includes(query));
    this.filteredKnittingOrders.set(filtered);
  }

  getKnittingOrderNumber(knittingOrderId?: string | null): string {
    if (!knittingOrderId) return '';
    return this.knittingOrdersMap[knittingOrderId]?.knittingOrderNumber || knittingOrderId;
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters as RequisitionFilters;
    this.currentPage = 1;
    this.search();
  }

  redirectToLotDetail(id?: string, bodegaVirtualInicialTejido?: boolean): void {
    const queryParams = id ? { id: id, bodegaVirtualInicialTejido: bodegaVirtualInicialTejido } : { id: 'new' };
    this.router.navigate(['/yarn-requisition-details'], { queryParams });
  }

  getItemsInRequisition(requisition: Requisition): string {
    const distinctEntries = _.uniqBy(requisition.requisitionEntries, 'itemCode');
    return _.join(
      _.map(
        distinctEntries,
        (entry) => `${entry.itemCode} (${_.round(entry.requiredQuantityInUnitOfMeasure, 2)} ${this.unitOfMeasure[entry.unitOfMeasure]})`,
      ),
      ', ',
    );
  }
}
